import { observer } from 'mobx-react-lite'
import { FC, useContext, useState } from 'react'
import { useDrag } from 'react-dnd'
import { IArpMedia } from '../../../../features/arp-registry/ArpMedia'
import { useTreeState } from '../../../stores/Root'
import { Column, Row, TagButton, VerticalCenteredRow } from '../../core/Components'
import { getSampleCategoryColor, gray1, gray13, gray15, gray6, red4 } from '../../platform/color'
import { FeaturedIcon } from '../../platform/icons'
import { CurrentlyPlayingContext, CurrentlyPlayingTypes } from '../Studio'
import { ALLOW_STEM_AND_SONG_PREVIEW } from '../StudioActivityBar'
import { StudioDraggableTypes } from '../StudioUtils'

export const getStemTitle = (stem: IArpMedia): string => {
  const parentStemName = stem.metadata.title.substring(0, stem.metadata.title.lastIndexOf(' - '))
  const title = stem?.user?.displayName
    ? `${parentStemName} by ${stem.user.displayName}`
    : parentStemName
  return title
}

interface IStemItem {
  stem: IArpMedia
  isSubitem?: boolean
}

const StemItem: FC<IStemItem> = observer(({ stem }) => {
  const [currentlyPlaying, setCurrentlyPlaying] = useContext(CurrentlyPlayingContext)
  const [hover, setHover] = useState<boolean>(false)
  const { studio } = useTreeState()

  const [{ isDragging }, drag] = useDrag(() => ({
    type: StudioDraggableTypes.STEM,
    item: { type: StudioDraggableTypes.STEM, stem },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging()
    })
  }))

  const isPlaying =
    currentlyPlaying.type === CurrentlyPlayingTypes.STEM && currentlyPlaying.index === stem.id
  const title = getStemTitle(stem)
  const category = stem.category
  const categoryColor = getSampleCategoryColor(category)
  return (
    <Row
      ref={drag}
      title={title}
      css={{
        marginBottom: '15px',
        overflowX: 'hidden'
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() =>
        ALLOW_STEM_AND_SONG_PREVIEW && setCurrentlyPlaying(CurrentlyPlayingTypes.STEM, stem)
      }
    >
      <Column>
        <VerticalCenteredRow
          className="fs-14 font-dm-sans fw-medium"
          css={{ color: hover || isPlaying ? gray1 : gray6, marginBottom: '10px' }}
        >
          <VerticalCenteredRow style={{ width: '15px', height: '15px' }}>
            {(isPlaying || hover) && (
              <img
                src={
                  ALLOW_STEM_AND_SONG_PREVIEW
                    ? `/static/icons/${
                        isPlaying ? 'studio_pause_circle' : 'studio_play_circle'
                      }.svg`
                    : '/static/icons/stem_drawer_icon.svg'
                }
                style={{ width: '15px', height: '15px' }}
              />
            )}
          </VerticalCenteredRow>
          <span
            style={{
              marginLeft: '15px'
            }}
            className="ellipsis-overflow"
          >
            {title}
          </span>
        </VerticalCenteredRow>
        <VerticalCenteredRow css={{ marginLeft: '30px' }} className="fs-12 fw-bold font-dm-sans">
          {category && (
            <TagButton
              backgroundColor={categoryColor}
              color={gray15}
              style={{ marginRight: '8px' }}
            >
              {category}
            </TagButton>
          )}
          {stem.remixCount > -1 && (
            <TagButton backgroundColor={gray13} color="white">
              <FeaturedIcon
                style={{ width: '14px', height: '14px', color: red4, marginRight: '5px' }}
              />
              {stem.remixCount}
            </TagButton>
          )}
        </VerticalCenteredRow>
      </Column>
    </Row>
  )
})

export default StemItem
